import React, { useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Smartphone, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { phoneCaptureAPI } from '@/lib/api';

const b64ToFile = (data, mime, name) => {
    const bin = atob(data);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new File([arr], name, { type: mime || 'image/jpeg' });
};

export function PhoneCaptureButton({
    onPhotos,
    multiple = true,
    label = 'Avec le téléphone',
    variant = 'outline',
    size = 'default',
    className = '',
    disabled = false,
    testId = 'phone-capture-btn',
}) {
    const [open, setOpen] = useState(false);
    const [session, setSession] = useState(null);
    const [photos, setPhotos] = useState([]);
    const photosRef = useRef([]);
    const lastSeqRef = useRef(-1);
    const finishedRef = useRef(false);

    const start = async () => {
        setPhotos([]);
        photosRef.current = [];
        lastSeqRef.current = -1;
        finishedRef.current = false;
        setSession(null);
        setOpen(true);
        try {
            const res = await phoneCaptureAPI.createSession(multiple);
            setSession(res.data);
        } catch {
            toast.error('Impossible de créer la session téléphone');
            setOpen(false);
        }
    };

    const finish = (deleteRemote = true) => {
        if (finishedRef.current) return;
        finishedRef.current = true;
        if (deleteRemote && session) phoneCaptureAPI.deleteSession(session.id, session.token).catch(() => {});
        const files = photosRef.current.map(p => p.file);
        setOpen(false);
        if (files.length) {
            onPhotos(files);
            toast.success(`${files.length} photo(s) récupérée(s) depuis le téléphone`);
        }
    };

    const cancel = () => {
        // Si des photos sont déjà arrivées, on les transmet au lieu de les jeter
        if (photosRef.current.length > 0) { finish(true); return; }
        finishedRef.current = true;
        if (session) phoneCaptureAPI.deleteSession(session.id, session.token).catch(() => {});
        setOpen(false);
    };

    useEffect(() => {
        if (!open || !session) return;
        let stopped = false;
        const tick = async () => {
            try {
                const res = await phoneCaptureAPI.getPhotos(session.id, session.token, lastSeqRef.current);
                if (stopped || finishedRef.current) return;
                const news = (res.data.photos || []).map(p => {
                    const file = b64ToFile(p.data, p.mime, `photo-telephone-${p.seq}.jpg`);
                    return { seq: p.seq, file, url: URL.createObjectURL(file) };
                });
                if (news.length) {
                    lastSeqRef.current = news[news.length - 1].seq;
                    photosRef.current = [...photosRef.current, ...news];
                    setPhotos([...photosRef.current]);
                }
                if (res.data.done) finish(true);
            } catch { /* on réessaie au prochain tick */ }
        };
        const iv = setInterval(tick, 2500);
        tick();
        return () => { stopped = true; clearInterval(iv); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, session]);

    const captureUrl = session ? `${window.location.origin}/capture/${session.id}?t=${session.token}` : '';

    return (
        <>
            <Button type="button" variant={variant} size={size} disabled={disabled}
                className={`hidden sm:inline-flex border-sky-300 text-sky-700 hover:bg-sky-50 ${className}`}
                onClick={start} data-testid={testId}>
                <Smartphone className="w-4 h-4 mr-1.5" /> {label}
            </Button>
            <Dialog open={open} onOpenChange={(v) => { if (!v) cancel(); }}>
                <DialogContent className="sm:max-w-md" data-testid="phone-capture-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Smartphone className="w-5 h-5 text-sky-600" /> Prendre avec le téléphone
                        </DialogTitle>
                        <DialogDescription>
                            Scannez ce QR code avec l'appareil photo du téléphone : une page de prise de photo
                            s'ouvre (sans connexion). Les photos arrivent ici automatiquement.
                        </DialogDescription>
                    </DialogHeader>
                    {!session ? (
                        <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>
                    ) : (
                        <div className="space-y-4">
                            <div className="flex justify-center">
                                <div className="bg-white p-3 rounded-xl border-2 border-slate-200" data-testid="phone-capture-qr">
                                    <QRCodeSVG value={captureUrl} size={210} level="M" />
                                </div>
                            </div>
                            <div className="flex justify-center">
                                <button
                                    type="button"
                                    className="text-[11px] text-sky-600 underline underline-offset-2 break-all max-w-full"
                                    onClick={() => { navigator.clipboard?.writeText(captureUrl); toast.success('Lien copié'); }}
                                    title={captureUrl}
                                    data-testid="phone-capture-link"
                                >
                                    Copier le lien (si le scan ne marche pas)
                                </button>
                            </div>
                            <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                                {photos.length === 0 ? (
                                    <><Loader2 className="w-4 h-4 animate-spin" /> En attente des photos du téléphone…</>
                                ) : (
                                    <span className="font-semibold text-emerald-600 flex items-center gap-1.5">
                                        <Check className="w-4 h-4" /> {photos.length} photo(s) reçue(s)
                                    </span>
                                )}
                            </div>
                            {photos.length > 0 && (
                                <div className="flex flex-wrap gap-2 justify-center max-h-32 overflow-y-auto">
                                    {photos.map(p => (
                                        <img key={p.seq} src={p.url} alt={`Photo ${p.seq}`} className="h-16 w-14 object-cover rounded border" />
                                    ))}
                                </div>
                            )}
                            {photos.length > 0 && (
                                <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => finish(true)} data-testid="phone-capture-use-btn">
                                    <Check className="w-4 h-4 mr-2" /> Utiliser {photos.length > 1 ? `les ${photos.length} photos` : 'la photo'} maintenant
                                </Button>
                            )}
                            <p className="text-[11px] text-slate-400 text-center">
                                Lien valable 1 h. Appuyez sur « Terminé » sur le téléphone pour valider automatiquement.
                            </p>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}
